import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";
import QrButton from "@/components/QrButton";
import {
  ArrowLeft, Copy, ExternalLink, Clock, CheckCircle2, XCircle, Ban,
  CreditCard, User, Phone, ShieldCheck, AlertCircle, Loader2, RefreshCw,
  ChevronRight,
} from "lucide-react";

const fmtIST = (iso) => {
  if (!iso) return "—";
  try { const d = new Date(iso); return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }); }
  catch { return "—"; }
};

const STATUS_COLOR = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  active: "bg-blue-50 text-blue-700 border-blue-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-stone-50 text-stone-600 border-stone-200",
  revoked: "bg-rose-50 text-rose-700 border-rose-200",
  inactive: "bg-orange-50 text-orange-700 border-orange-200",
  deactivated: "bg-gray-100 text-gray-600 border-gray-300",
  failed: "bg-red-50 text-red-700 border-red-200",
};

export default function PaymentLinkDetail() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [payerSessions, setPayerSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pgResponses, setPgResponses] = useState(null);

  useEffect(() => {
    api.get(`/admin/payment-links/${linkId}`)
      .then(({ data }) => { setLink(data.link); setPayerSessions(data.payer_sessions || []); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  }, [linkId]);

  const linkUrl = link ? `${(process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "")}/pay/${link.id}` : "";

  const copyUrl = () => copyToClipboard(linkUrl, { successMessage: "URL copied" });

  const refreshStatus = async () => {
    if (!link) return;
    setRefreshing(true);
    try {
      const { data } = await api.post(`/admin/payment-links/${link.id}/refresh-status`);
      setLink(data.link);
      setPgResponses(data.pg_responses || null);
      toast.success(`Status: ${data.status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></DashboardLayout>;
  if (!link) return <DashboardLayout><div className="text-center py-20 text-muted-foreground">Link not found</div></DashboardLayout>;

  const ps = link.partner_snapshot || {};
  const sc = STATUS_COLOR[link.status] || STATUS_COLOR.pending;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="payment-link-detail">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="h-4 w-4" /></Button>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold tracking-tight">Payment Link Detail</h1>
            <p className="text-xs text-muted-foreground font-mono mt-0.5">{link.id}</p>
          </div>
          <Badge variant="outline" className={`text-sm px-3 py-1 ${sc}`}>{link.status}</Badge>
          {link.status === "pending" && (
            <Button
              variant="outline"
              size="sm"
              onClick={refreshStatus}
              disabled={refreshing}
              data-testid="refresh-status-btn"
              title="Refresh status from gateway"
            >
              {refreshing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              Refresh status
            </Button>
          )}
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <InfoCard icon={CreditCard} label="Amount" value={link.self_amount ? "Self-amount" : `₹${Number(link.amount || 0).toLocaleString("en-IN")}`} />
          <InfoCard icon={Clock} label="Created" value={fmtIST(link.created_at)} />
          <InfoCard icon={Clock} label="Expires" value={fmtIST(link.expires_at)} />
          <InfoCard icon={CheckCircle2} label="Gateway" value={(link.gateway || "—").toUpperCase()} sub={link.pg_channel ? `Channel: ${link.pg_channel}` : null} />
        </div>

        {/* Detail Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Link Details */}
          <div className="surface-card p-5 space-y-3">
            <h3 className="font-display font-semibold text-sm flex items-center gap-2"><CreditCard className="h-4 w-4" /> Link Configuration</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Type" value={<Badge variant="outline" className="text-[10px]">{link.type}</Badge>} />
              <Field label="Status" value={<Badge variant="outline" className={`text-[10px] ${sc}`}>{link.status}</Badge>} />
              <Field label="Amount" value={link.self_amount ? "User enters amount" : `₹${Number(link.amount || 0).toLocaleString("en-IN")}`} />
              <Field label="Self-amount" value={link.self_amount ? "Yes" : "No"} />
              {link.min_amount != null && <Field label="Min amount" value={`₹${Number(link.min_amount).toLocaleString("en-IN")}`} />}
              {link.max_amount != null && <Field label="Max amount" value={`₹${Number(link.max_amount).toLocaleString("en-IN")}`} />}
              {link.suggested_amounts?.length > 0 && <Field label="Suggested" value={link.suggested_amounts.map((a) => `₹${a}`).join(", ")} />}
              <Field label="Gateway" value={link.gateway} />
              {link.pg_channel && <Field label="PG Channel" value={link.pg_channel} />}
              <Field label="Max attempts" value={link.max_attempts || "Unlimited"} />
              <Field label="Max successful" value={link.max_successful_payments || 1} />
              <Field label="Attempts used" value={link.attempt_count || 0} />
              <Field label="Successful" value={link.successful_count || 0} />
              <Field label="Expires in" value={`${link.expires_in_hours || 24}h`} />
              {link.remarks && <Field label="Remarks" value={link.remarks} className="col-span-2" />}
            </div>
            {link.id && (
              <div className="flex items-center gap-2 pt-2 border-t">
                <code className="text-[11px] font-mono bg-muted rounded px-2 py-1 flex-1 truncate">{linkUrl}</code>
                <Button variant="ghost" size="sm" onClick={copyUrl}><Copy className="h-3.5 w-3.5" /></Button>
                <QrButton url={linkUrl} filename={`payment-link-${link.id?.slice(0, 8)}.png`} title="Show QR code" testId="detail-qr-btn" />
                <Button variant="ghost" size="sm" onClick={() => window.open(linkUrl, "_blank")}><ExternalLink className="h-3.5 w-3.5" /></Button>
              </div>
            )}
          </div>

          {/* Partner / Creator */}
          <div className="surface-card p-5 space-y-3">
            <h3 className="font-display font-semibold text-sm flex items-center gap-2"><User className="h-4 w-4" /> Partner & Creator</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {ps.full_name && <Field label="Partner name" value={ps.full_name} />}
              {ps.email && <Field label="Partner email" value={ps.email} />}
              {ps.mobile && <Field label="Partner mobile" value={ps.mobile} />}
              <Field label="Created by" value={link.created_by_email || link.created_by || "—"} />
              <Field label="Created at" value={fmtIST(link.created_at)} />
              {link.paid_at && <Field label="Paid at" value={fmtIST(link.paid_at)} />}
              {link.paid_amount && <Field label="Paid amount" value={`₹${Number(link.paid_amount).toLocaleString("en-IN")}`} />}
              {link.provider_order_id && <Field label="PG Order ID" value={link.provider_order_id} />}
              {link.provider_ref && <Field label="PG Reference" value={link.provider_ref} />}
              {link.revoked_at && <Field label="Revoked at" value={fmtIST(link.revoked_at)} />}
              {link.revoke_reason && <Field label="Revoke reason" value={link.revoke_reason} />}
            </div>
          </div>
        </div>

        {/* Payer Sessions / Payment Attempts */}
        <div className="surface-card p-5">
          <h3 className="font-display font-semibold text-sm flex items-center gap-2 mb-3"><Phone className="h-4 w-4" /> Payer Sessions & Attempts</h3>
          {payerSessions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No payer sessions recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-3 py-2 font-medium">Mobile</th>
                    <th className="text-left px-3 py-2 font-medium">OTP Verified</th>
                    <th className="text-left px-3 py-2 font-medium">OTP Attempts</th>
                    <th className="text-left px-3 py-2 font-medium">Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {payerSessions.map((ps, i) => (
                    <tr key={i} className="border-b">
                      <td className="px-3 py-2 font-mono">{ps.mobile || "—"}</td>
                      <td className="px-3 py-2">
                        {ps.verified
                          ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]"><ShieldCheck className="h-3 w-3 mr-1" /> Verified</Badge>
                          : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Pending</Badge>}
                      </td>
                      <td className="px-3 py-2 font-mono">{ps.attempts || 0}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{fmtIST(ps.expires_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* PG status responses — only shown after admin clicks Refresh */}
        {pgResponses && (
          <div className="surface-card p-5" data-testid="pg-status-panel">
            <h3 className="font-display font-semibold text-sm flex items-center gap-2 mb-4">
              <RefreshCw className="h-4 w-4" /> Gateway Status (raw responses)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PgResponseCard
                title="pgtxns_status"
                subtitle="v1 — by txnid (primary, rich payload)"
                payload={pgResponses.pgtxns_status}
              />
              <PgResponseCard
                title="transactionstatus"
                subtitle="basic — by referenceid (audit only)"
                payload={pgResponses.transactionstatus}
              />
            </div>
            {pgResponses.error && (
              <p className="text-xs text-red-600 mt-3 flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" />
                {pgResponses.error}
              </p>
            )}
          </div>
        )}
        {/* Payment sessions — each clicked-Pay attempt creates an entry here.
            Click a row to expand and reveal the rich payment details captured from the webhook. */}
        {Array.isArray(link.payment_sessions) && link.payment_sessions.length > 0 && (
          <div className="surface-card p-5" data-testid="payment-sessions-panel">
            <h3 className="font-display font-semibold text-sm flex items-center gap-2 mb-4">
              <RefreshCw className="h-4 w-4" /> Payment Sessions ({link.payment_sessions.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-widest">
                  <tr>
                    <th className="w-6"></th>
                    <th className="text-left px-3 py-2 font-medium">When</th>
                    <th className="text-left px-3 py-2 font-medium">Gateway</th>
                    <th className="text-left px-3 py-2 font-medium">PG Channel</th>
                    <th className="text-left px-3 py-2 font-medium">Provider Order ID</th>
                    <th className="text-left px-3 py-2 font-medium">Amount</th>
                    <th className="text-left px-3 py-2 font-medium">Mobile</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-left px-3 py-2 font-medium">UTR</th>
                  </tr>
                </thead>
                <tbody>
                  {link.payment_sessions.slice().reverse().map((s, i) => (
                    <SessionRow key={i} idx={i} session={s} />
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              Most recent first. Click a row to expand and view the captured payment details (issuer, card, network, UTR, …).
            </p>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}

function SessionRow({ idx, session: s }) {
  const [open, setOpen] = useState(false);
  const sc = STATUS_COLOR[s.status] || STATUS_COLOR.pending;
  const hasDetails =
    s.utr || s.bank_name || s.card_last4 || s.card_network ||
    s.card_subtype || s.method_type || s.mode_pg || s.pgtype || s.remarks;
  return (
    <>
      <tr
        className={`border-b transition-colors ${hasDetails ? "cursor-pointer hover:bg-accent/40" : ""}`}
        data-testid={`session-row-${idx}`}
        onClick={() => hasDetails && setOpen(!open)}
        title={hasDetails ? (open ? "Click to collapse" : "Click to expand payment details") : ""}
      >
        <td className="px-2 py-2 text-muted-foreground">
          {hasDetails ? (
            <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
          ) : (
            <span className="opacity-30">·</span>
          )}
        </td>
        <td className="px-3 py-2 text-xs text-muted-foreground">{fmtIST(s.created_at)}</td>
        <td className="px-3 py-2 capitalize">{s.gateway || "—"}</td>
        <td className="px-3 py-2 font-mono text-xs">{s.pg_channel || "—"}</td>
        <td className="px-3 py-2 font-mono text-xs">{s.provider_order_id || "—"}</td>
        <td className="px-3 py-2 font-medium">₹{s.amount}</td>
        <td className="px-3 py-2 font-mono text-xs">{s.mobile || "—"}</td>
        <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${sc}`}>{s.status}</Badge></td>
        <td className="px-3 py-2 font-mono text-xs">{s.utr || s.provider_ref || "—"}</td>
      </tr>
      {hasDetails && open && (
        <tr className="bg-muted/20 border-b" data-testid={`session-detail-${idx}`}>
          <td></td>
          <td colSpan={8} className="px-4 py-4">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
              <CreditCard className="h-3.5 w-3.5" /> Payment Details
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
              <DField label="UTR / Bank ref" value={s.utr || s.provider_ref} mono />
              <DField label="Issuer / Bank" value={s.bank_name} />
              <DField label="Card last 4" value={s.card_last4} mono />
              <DField label="Network / Scheme" value={s.card_network || s.card_subtype} />
              <DField label="Method" value={s.method_type} />
              <DField label="PG channel (mode)" value={s.mode_pg} mono />
              <DField label="PG type" value={s.pgtype} />
              <DField label="Settled at" value={fmtIST(s.settled_at)} />
              <DField label="Payer name" value={s.payer_name} />
              <DField label="Txn date (PG)" value={s.dateadded} mono />
              {s.remarks && (
                <div className="col-span-2 md:col-span-4">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Remarks</div>
                  <div className="text-xs italic text-foreground/80 mt-0.5">{s.remarks}</div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DField({ label, value, mono = false }) {
  const display = value ? (mono ? <code className="font-mono">{value}</code> : value) : "—";
  return (
    <div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{label}</div>
      <div className={`text-xs mt-0.5 ${value ? "font-medium" : "text-muted-foreground"}`}>{display}</div>
    </div>
  );
}

function PgResponseCard({ title, subtitle, payload }) {
  const status = String(payload?.data?.status ?? "");
  const verdictMap = { "1": "success", "0": "failed", "2": "pending", "3": "refund", "5": "refund" };
  const verdict = verdictMap[status] || (payload?.data ? `unknown (status=${status})` : "—");
  const verdictClass =
    status === "1" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    status === "0" ? "bg-red-50 text-red-700 border-red-200" :
    status === "2" ? "bg-amber-50 text-amber-700 border-amber-200" :
    "bg-stone-100 text-stone-600 border-stone-200";
  return (
    <div className="rounded-xl border bg-muted/30 p-4">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div>
          <p className="text-sm font-semibold font-mono">{title}</p>
          <p className="text-[11px] text-muted-foreground">{subtitle}</p>
        </div>
        {payload && (
          <Badge variant="outline" className={`text-[10px] ${verdictClass}`}>
            {verdict}
          </Badge>
        )}
      </div>
      <div className="mt-2 space-y-1 text-xs">
        <div><span className="text-muted-foreground">message:</span> <code className="font-mono">{payload?.message || "—"}</code></div>
        {payload?.data?.remarks && (
          <div><span className="text-muted-foreground">remarks:</span> <code className="font-mono">{payload.data.remarks}</code></div>
        )}
        {payload?.data?.utr && (
          <div><span className="text-muted-foreground">utr:</span> <code className="font-mono">{payload.data.utr}</code></div>
        )}
      </div>
      <details className="mt-3">
        <summary className="text-[11px] text-muted-foreground cursor-pointer hover:underline">View raw JSON</summary>
        <pre className="text-[10px] font-mono bg-stone-900 text-stone-100 rounded p-2 mt-2 max-h-48 overflow-auto">
{JSON.stringify(payload, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="surface-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className="font-semibold text-lg">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Field({ label, value, className = "" }) {
  return (
    <div className={className}>
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium mt-0.5">{typeof value === "string" ? value : value}</dd>
    </div>
  );
}
